import { useTranslation } from "react-i18next";
import { CITIES, type City } from "@nifgash/shared/types";

type Props = {
  value: City | null;
  onChange: (city: City | null) => void;
  /** Show an "All cities" option (used in the events filter). */
  allowAll?: boolean;
};

/**
 * Native <select> for picking a city. With ~27 cities, chips no longer fit;
 * a dropdown stays compact and uses the platform's native picker (good on
 * mobile). Options are sorted by the current language's label so the list
 * reads alphabetically in whatever locale is active.
 */
export function CitySelect({ value, onChange, allowAll = false }: Props) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  const options = [...CITIES]
    .map((c) => ({ value: c, label: t(`labels.city.${c}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange((e.target.value || null) as City | null)}
      className="w-full h-13 px-4 text-base bg-ink-50 border border-ink-200 rounded-[var(--radius-md)] text-ink-900 focus:outline-none focus:border-brand-purple focus:bg-surface focus:ring-4 focus:ring-brand-purple/10 transition-all duration-150 appearance-none"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat",
        // Arrow sits at the trailing edge — right in LTR, left in RTL — and
        // the matching inline-end padding keeps the text off it in both.
        backgroundPosition: isRtl ? "left 12px center" : "right 12px center",
        paddingInlineEnd: "40px",
      }}
    >
      {allowAll && <option value="">{t("events.filterAll")}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
